import {
    Box,
    Divider,
    Drawer,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material'
import { FeatureFlagIdAgentConversations, FeatureFlagIdExportData, FeatureFlagIdOauth2PersonalClients, FeatureFlagIdAccountProvisioning, FeatureFlagIdOauth2Dcr, FeatureFlagIdPersonalAndOrgNotificationSettings } from 'c1gen/features'
import { ConversationsV1UiconversationsServiceCreate } from 'c1gen/permissions'
import { C1IconButton, sidebarCollapsedWidth } from 'c1ui'
import { C1Transitions } from 'c1ui/theme/C1Transitions'
import { UserAvatar } from 'components/core/avatar'
import { AgenticAiIcon } from 'components/core/icons/AgenticAiIcon'
import { C1Icon } from 'components/core/icons/C1Icon'
import { C1Link } from 'components/core/links/C1Link'
import { createURL } from 'components/core/links/docs'
import { navigationConfig } from 'components/modular-nav/config'
import { ModuleFlyout } from 'components/modular-nav/ModuleFlyout'
import { NavItemDisplay, useFlyoutClose } from 'components/modular-nav/NavItem'
import { navItemMinHeightPx, navItemWrapperXPaddingPx, navSidebarWidthCollapsed, navSidebarWidthExpanded, navTransitionDuration, NavContainer } from 'components/modular-nav/types'
import { UserMenuOverlay } from 'components/modular-nav/UserMenuOverlay'
import { HasCapability, useFeatureChecker, useHasAnyBuiltinRole, useHasBuiltinRole, useRoleChecker, useUnsafeFeatureChecker } from 'components/no-ui/capability'
import { useSystemManagementWarning } from 'components/pages/admin/settings/system-management/SystemManagementWarningIndicator'
import { SkipNavigation } from 'components/pages/app-bar/SkipNavigation'
import {
    COLLAPSED_SIDEBAR_NAV_STORAGE_KEY,
} from 'components/pages/sidebar/CollapsingSidebar'
import { Downloads } from 'components/pages/sidebar/Downloads'
import { Spotlight } from 'components/pages/spotlight/Spotlight'
import { UserDisplayName } from 'components/pages/user/user-display-name'
import { useLocalStorageContext } from 'hooks/local-storage-context'
import { useSessionStorageContext } from 'hooks/session-storage-context'
import { useDrawer } from 'hooks/useDrawer'
import { MutedDotsCacheKey, RoleID } from 'lib/constants/system'
import { AdminModeContext, DashboardContext, UserContext } from 'lib/context/app'
import { handleLogout } from 'lib/oauth'
import { routes } from 'lib/routes'
import { useOAuth } from 'lib/rpc'
import {
    ArrowRight,
    Bell,
    BookOpen,
    Download,
    Home,
    Key,
    Lock,
    LogOut,

    Search,
} from 'lucide-react'
import { useRouter } from 'next/router'
import { BrandingConfiguration } from 'pbts/c1/api/settings/v1/branding'
import { User } from 'pbts/c1/api/user/v1/user'
import { createContext, FC, ReactElement, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { introspectBrandingApi } from 'redux_toolkit/branding'

// Context for system management warning state
type SystemManagementWarningContextType = {
    showWarning: boolean
    tooltipContent: ReactNode | null
}

const SystemManagementWarningContext = createContext<SystemManagementWarningContextType>({
    showWarning: false,
    tooltipContent: null,
})

export const useSystemManagementWarningContext = (): SystemManagementWarningContextType =>
    useContext(SystemManagementWarningContext)

const sidebarTransitionEasing = C1Transitions.easing.linearSpringA

const getTransition = (properties: string): object => ({
    transitionProperty: properties,
    transitionDuration: navTransitionDuration,
    transitionTimingFunction: sidebarTransitionEasing,
})

const newSidebarWidth = navSidebarWidthExpanded
const newSidebarCollapsedWidth = navSidebarWidthCollapsed

const ICON_LOGO_URL = '/dapi/static/images/branding/favicon/current/32'

const getIconSrc = (brandingData: BrandingConfiguration | undefined): string => {
    return `${ICON_LOGO_URL}?${brandingData?.favicon?.src}&${brandingData?.enabled}`
}

export const ModularC1Nav: FC = () => {
    const intl = useIntl()
    const router = useRouter()
    const oauth = useOAuth()
    const [showDrawer] = useDrawer()
    const [isCollapsedState, setIsCollapsed] = useLocalStorageContext(COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, false)
    const isCollapsed = isCollapsedState ?? false
    const [searchOpen, setSearchOpen] = useState(false)
    const dashboardContext = useContext(DashboardContext)
    const userContext = useContext(UserContext)
    const adminModeContext = useContext(AdminModeContext)
    const user = userContext.user
    const [userMenuFlyoutRef, setUserMenuFlyoutRef] = useState<HTMLDivElement | null>(null)

    const hasAgentConversationsFF = useFeatureChecker(FeatureFlagIdAgentConversations)
    const hasExportDataFF = useFeatureChecker(FeatureFlagIdExportData)
    const hasApiKeysFF = useFeatureChecker(FeatureFlagIdOauth2PersonalClients)
    const hasAccountProvisioningFF = useFeatureChecker(FeatureFlagIdAccountProvisioning)
    const hasDcrFF = useFeatureChecker(FeatureFlagIdOauth2Dcr)
    const hasRoleGreaterThanBasicUser = useHasAnyBuiltinRole(Object.values(RoleID).filter((role) => role !== RoleID.BasicUser))
    const hasPersonalAndOrgNotificationSettingsFF = useFeatureChecker(FeatureFlagIdPersonalAndOrgNotificationSettings)

    const { data: brandingData } = introspectBrandingApi.useIntrospect({}, { notify: true })

    // Mute notification dots when navigating to a page that matches a nav module
    const [, setMutedDotsCache] = useSessionStorageContext(MutedDotsCacheKey, {})
    useEffect(() => {
        // Escape special regex characters for literal path matching
        const escapeRegex = (str: string): string => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

        const allModules = navigationConfig.flatMap((c) => c.modules)
        const matchingModules = allModules.filter((navModule) => {
            const paths = [navModule.pathname, ...(navModule.selectedPaths || [])]
            return paths.some((path) => {
                // Exact match for pathname
                if (router.pathname === path || router.asPath === path) {
                    return true
                }
                // Prefix match for selectedPaths
                if (navModule.selectedPaths?.includes(path)) {
                    const escapedPath = escapeRegex(path)
                    const regex = new RegExp(`^${escapedPath}(?:/|$).*$`)
                    return regex.test(router.pathname) || regex.test(router.asPath)
                }
                return false
            })
        })

        if (matchingModules.length > 0) {
            const updates = matchingModules.reduce<Record<string, boolean>>((acc, m) => {
                acc[m.id] = true
                return acc
            }, {})
            setMutedDotsCache((prev) => ({ ...prev, ...updates }))
        }
    }, [router.pathname, router.asPath, setMutedDotsCache])

    // System management warning state
    const { showWarning: showSystemManagementWarning, disabledSettings } = useSystemManagementWarning()
    const systemManagementWarningTooltip = useMemo(() => (showSystemManagementWarning ? (
        <Box>
            <FormattedMessage
                id="system_management_warning_tooltip_title"
                defaultMessage="System features disabled:"
            />
            <ul style={{ margin: '4px 0', paddingLeft: '16px' }}>
                {disabledSettings.map((setting, index) => (
                    <li key={index}>{setting}</li>
                ))}
            </ul>
        </Box>
    ) : null), [showSystemManagementWarning, disabledSettings])
    const systemManagementWarningContextValue = useMemo(() => ({
        showWarning: showSystemManagementWarning,
        tooltipContent: systemManagementWarningTooltip,
    }), [showSystemManagementWarning, systemManagementWarningTooltip])

    // Keyboard shortcut for spotlight search (Cmd/Ctrl + K)
    const handleKeyPress = useCallback((event: KeyboardEvent): void => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
            event.preventDefault()
            event.stopPropagation()
            setSearchOpen(true)
        }
    }, [])

    useEffect(() => {
        window.addEventListener('keydown', handleKeyPress)
        return () => {
            window.removeEventListener('keydown', handleKeyPress)
        }
    }, [handleKeyPress])

    const width = isCollapsed ? newSidebarCollapsedWidth : newSidebarWidth

    const isContainerPersistent = (container: NavContainer): boolean => {
        if (!container.persistentPaths?.length) return false
        return container.persistentPaths.some((path) =>
            router.pathname.startsWith(path) || router.asPath.startsWith(path))
    }

    const getCountForModule = (moduleId: string): number | undefined => {
        switch (moduleId) {
            case 'requests':
                return (
                    (dashboardContext.get('my_requests') || 0) +
                    (dashboardContext.get('request_tasks') || 0) +
                    (dashboardContext.get('enrollment_tasks') || 0) +
                    (dashboardContext.get('revoke_tasks') || 0)
                )
            case 'reviews':
                return dashboardContext.get('review_tasks')
            case 'access-conflicts':
                return dashboardContext.get('conflicts')
            default:
                return undefined
        }
    }

    // Check if any module across all containers claims the current path
    // This prevents broad persistentPaths from highlighting when a specific module matches
    const isPathClaimedByAnyModule = (): boolean => {
        const currentPath = router.pathname
        const currentAsPath = router.asPath
        return navigationConfig.some((navContainer) => navContainer.modules.some((navModule) => {
            const paths = [navModule.pathname, ...(navModule.selectedPaths || [])]
            return paths.some((path) => {
                const regex = new RegExp(`^${path.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}.*$`)
                return regex.test(currentPath) || regex.test(currentAsPath)
            })
        }))
    }

    const onLogout = (): void => {
        handleLogout(oauth)
    }

    const onOpenDocs = (): void => {
        window.open(createURL('/docs/product/intro') || 'https://www.conductorone.com/docs/product/intro/', '_blank')
    }

    const onOpenDownloads = (): void => {
        showDrawer(<Downloads />)
    }

    // Ref for the sidebar container for keyboard navigation
    const sidebarRef = useRef<HTMLDivElement>(null)

    // Ref and state for detecting when nav needs scrolling
    const navRef = useRef<HTMLElement>(null)
    const [navNeedsScroll, setNavNeedsScroll] = useState(false)
    const [navScrolledToBottom, setNavScrolledToBottom] = useState(false)

    useEffect(() => {
        const navElement = navRef.current
        if (!navElement) return

        const checkScroll = (): void => {
            const needsScroll = navElement.scrollHeight > navElement.clientHeight
            setNavNeedsScroll(needsScroll)
            if (needsScroll) {
                const atBottom = navElement.scrollTop + navElement.clientHeight >= navElement.scrollHeight - 1
                setNavScrolledToBottom(atBottom)
            }
        }

        checkScroll()
        const resizeObserver = new ResizeObserver(checkScroll)
        resizeObserver.observe(navElement)
        navElement.addEventListener('scroll', checkScroll)

        return () => {
            resizeObserver.disconnect()
            navElement.removeEventListener('scroll', checkScroll)
        }
    }, [])

    // Handle arrow key navigation within the sidebar
    const handleNavKeyDown = useCallback((e: React.KeyboardEvent): void => {
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return

        const currentTarget = e.target as HTMLElement
        const navIdElement = currentTarget.closest('[data-nav-id]')
        if (!navIdElement) return

        // Query DOM for all rendered nav items (respects role/feature visibility)
        const allNavItems = sidebarRef.current?.querySelectorAll('[data-nav-id]')
        if (!allNavItems) return

        const navItemsArray = Array.from(allNavItems)
        const currentIndex = navItemsArray.indexOf(navIdElement)
        if (currentIndex === -1) return

        e.preventDefault()
        const nextIndex = e.key === 'ArrowDown'
            ? Math.min(currentIndex + 1, navItemsArray.length - 1)
            : Math.max(currentIndex - 1, 0)

        if (nextIndex === currentIndex) return

        const nextElement = navItemsArray[nextIndex]
        if (nextElement) {
            // Find the focusable element inside (the .nav-item-clickable)
            const focusable = nextElement.querySelector('.nav-item-clickable') as HTMLElement
            if (focusable) {
                focusable.focus()
                // Scroll into view if needed
                focusable.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
            }
        }
    }, [])

    return (
        <SystemManagementWarningContext.Provider value={systemManagementWarningContextValue}>
            <SkipNavigation showSidebarLink showTopBarLink={false} />
            <Drawer
                variant="permanent"
                PaperProps={{
                    id: 'sidebar',
                    sx: {
                        overflow: 'visible',
                        zIndex: 1200,
                        width,
                        ...getTransition('width'),
                        borderRight: '1px solid',
                        borderRightColor: 'divider',
                        backgroundColor: 'background.default',
                        height: '100vh',
                    },
                }}
                sx={{ width, ...getTransition('width') }}
            >
                <Box
                    ref={sidebarRef}
                    onKeyDown={handleNavKeyDown}
                    sx={{
                        position: 'relative',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        // When any nav item has focus, hide flyouts on non-focused items (prevents hover overlap)
                        '&:has(:focus-within) [data-nav-id]:not(:focus-within) .nav-flyout': {
                            visibility: 'hidden !important',
                            pointerEvents: 'none !important',
                        },
                    }}
                >
                    <Box
                        sx={{
                            pt: isCollapsed ? '52px' : 4,
                            pb: isCollapsed ? 2 : 4,
                            px: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            ...getTransition('padding-top, padding-bottom'),
                        }}
                    >
                        <C1Link
                            href={routes.home}
                            aria-label={intl.formatMessage(
                                { id: 'components_app_bar_home_link', defaultMessage: 'Go to {displayname} home' },
                                { displayname: brandingData?.displayName || 'ConductorOne' },
                            )}
                        >
                            <Box
                                component="img"
                                alt={brandingData?.displayName || 'ConductorOne'}
                                src={getIconSrc(brandingData)}
                                height={24}
                                width={24}
                                style={{ display: 'block' }}
                            />
                        </C1Link>
                    </Box>

                    {hasRoleGreaterThanBasicUser && (
                        <SearchButton onClick={(): void => setSearchOpen(true)} />
                    )}

                    <Box
                        sx={{
                            flex: 1,
                            minHeight: 0,
                            position: 'relative',
                            ...(navNeedsScroll && {
                                borderTop: '1px solid',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                            }),
                        }}
                    >
                        <Box
                            component="nav"
                            ref={navRef}
                            aria-label={intl.formatMessage({
                                id: 'nav_main_navigation',
                                defaultMessage: 'Main navigation',
                            })}
                            sx={{
                                height: '100%',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                ...(navNeedsScroll && { pt: 1, pb: 2 }),
                            }}
                        >
                            <Tooltip
                                title={isCollapsed ? intl.formatMessage({ id: 'nav_home', defaultMessage: 'Home' }) : ''}
                                placement="right"
                                disableHoverListener={!isCollapsed}
                                enterDelay={0}
                                leaveDelay={0}
                            >
                                <Box data-nav-id="home">
                                    <NavItemDisplay
                                        label={<FormattedMessage id="nav_home" defaultMessage="Home" />}
                                        icon={<C1Icon icon={Home} size="sm" sx={{ color: router.pathname === '/' ? 'primary.extraDark' : 'text.primary' }} />}
                                        href={routes.home}
                                        isCollapsed={isCollapsed}
                                        isTopLevel
                                        isActive={router.pathname === '/'}
                                    />
                                </Box>
                            </Tooltip>
                            {navigationConfig.map((container): ReactElement => {
                                if (container.variant === 'button') {
                                    return (
                                        <Tooltip
                                            key={container.id}
                                            title={isCollapsed ? container.label : ''}
                                            placement="right"
                                            disableHoverListener={!isCollapsed}
                                            enterDelay={0}
                                            leaveDelay={0}
                                        >
                                            <Box data-nav-id={container.id}>
                                                <NavItemDisplay
                                                    label={container.label}
                                                    icon={<C1Icon icon={container.icon} size="sm" sx={{ transition: 'color 0.2s ease' }} />}
                                                    href={container.href}
                                                    isCollapsed={isCollapsed}
                                                    isTopLevel
                                                    variant="button"
                                                />
                                            </Box>
                                        </Tooltip>
                                    )
                                }
                                const isPersistent = isContainerPersistent(container)
                                return (
                                    <ContainerNavItem
                                        key={`${container.id}-${isPersistent}`}
                                        container={container}
                                        isCollapsed={isCollapsed}
                                        isPersistent={isPersistent}
                                        getCountForModule={getCountForModule}
                                        isPathClaimedByAnyModule={isPathClaimedByAnyModule}
                                    />
                                )
                            })}
                        </Box>
                        {/* Shadow overlay - positioned on top of scrollable content, hidden at bottom */}
                        {navNeedsScroll && !navScrolledToBottom && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: (theme) => theme.spacing(2),
                                    background: 'linear-gradient(to top, rgba(0, 0, 0, 0.04), transparent)',
                                    pointerEvents: 'none',
                                }}
                            />
                        )}
                    </Box>

                    <Box
                        sx={{
                            pt: 1,
                            pb: 1,
                            flexShrink: 0,
                        }}
                    >
                        {/* Thomas */}
                        {hasAgentConversationsFF && (
                            <HasCapability permission={ConversationsV1UiconversationsServiceCreate}>
                                <Tooltip
                                    title={isCollapsed ? intl.formatMessage({ id: 'nav_thomas', defaultMessage: 'Thomas' }) : ''}
                                    placement="right"
                                    disableHoverListener={!isCollapsed}
                                    enterDelay={0}
                                    leaveDelay={0}
                                >
                                    <Box data-nav-id="thomas">
                                        <NavItemDisplay
                                            label={<FormattedMessage id="nav_thomas" defaultMessage="Thomas" />}
                                            icon={<AgenticAiIcon sx={{ fontSize: 20, color: 'text.secondary', transform: isCollapsed ? 'translateX(-2px)' : undefined }} />}
                                            href={routes.conversations.new}
                                            isCollapsed={isCollapsed}
                                            isTopLevel
                                        />
                                    </Box>
                                </Tooltip>
                            </HasCapability>
                        )}

                        {/* Downloads */}
                        {hasExportDataFF && adminModeContext.hasAdminRole && (
                            <Tooltip
                                title={isCollapsed ? intl.formatMessage({ id: 'components_app_bar_download', defaultMessage: 'Downloads' }) : ''}
                                placement="right"
                                disableHoverListener={!isCollapsed}
                                enterDelay={0}
                                leaveDelay={0}
                            >
                                <Box data-nav-id="downloads">
                                    <NavItemDisplay
                                        label={<FormattedMessage id="components_app_bar_download" defaultMessage="Downloads" />}
                                        icon={<C1Icon icon={Download} size="sm" sx={{ color: 'text.secondary' }} />}
                                        onClick={onOpenDownloads}
                                        isCollapsed={isCollapsed}
                                        isTopLevel
                                    />
                                </Box>
                            </Tooltip>
                        )}

                        <Tooltip
                            title={isCollapsed ? intl.formatMessage({ id: 'components_app_bar_docs', defaultMessage: 'Documentation' }) : ''}
                            placement="right"
                            disableHoverListener={!isCollapsed}
                            enterDelay={0}
                            leaveDelay={0}
                        >
                            <Box data-nav-id="docs">
                                <NavItemDisplay
                                    label={<FormattedMessage id="components_app_bar_docs" defaultMessage="Docs" />}
                                    icon={<C1Icon icon={BookOpen} size="sm" sx={{ color: 'text.secondary' }} />}
                                    onClick={onOpenDocs}
                                    isCollapsed={isCollapsed}
                                    isTopLevel
                                />
                            </Box>
                        </Tooltip>

                        <Divider sx={{ my: 1 }} />

                        <Box data-nav-id="user-menu">
                            <NavItemDisplay
                                label={user?.displayName || user?.email}
                                icon={<UserAvatar user={user} size="sm" readOnly />}
                                isCollapsed={isCollapsed}
                                isTopLevel
                                flyoutRef={userMenuFlyoutRef}
                                ariaLabel={intl.formatMessage(
                                    { id: 'nav_user_menu', defaultMessage: 'User menu for {name}' },
                                    { name: user?.displayName || user?.email || intl.formatMessage({ id: 'nav_user', defaultMessage: 'user' }) },
                                )}
                                wrapperSx={{
                                    '& > .nav-item-clickable > .nav-item-highlight > .MuiStack-root > .MuiTypography-root': {
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: 100,
                                    },
                                }}
                            >
                                <UserMenuFlyout
                                    user={user}
                                    hasAccountProvisioningFF={!!hasAccountProvisioningFF}
                                    hasApiKeysFF={!!hasApiKeysFF}
                                    hasDcrFF={!!hasDcrFF}
                                    hasPersonalAndOrgNotificationSettingsFF={!!hasPersonalAndOrgNotificationSettingsFF}
                                    onLogout={onLogout}
                                    onRef={setUserMenuFlyoutRef}
                                />
                            </NavItemDisplay>
                        </Box>
                    </Box>
                </Box>
            </Drawer>

            {/* Outside sidebar stacking context for proper z-index */}
            <SidebarToggleButton isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />
            <Spotlight open={searchOpen} onClose={(): void => setSearchOpen(false)} />
        </SystemManagementWarningContext.Provider>
    )
}

type UserMenuFlyoutProps = {
    user: User | undefined
    hasAccountProvisioningFF: boolean
    hasApiKeysFF: boolean
    hasDcrFF: boolean
    hasPersonalAndOrgNotificationSettingsFF: boolean
    onLogout: () => void
    /** Callback to expose the flyout element ref for safe area calculation */
    onRef?: (ref: HTMLDivElement | null) => void
}

const UserMenuFlyout: FC<UserMenuFlyoutProps> = ({
    user,
    hasAccountProvisioningFF,
    hasApiKeysFF,
    hasDcrFF,
    hasPersonalAndOrgNotificationSettingsFF,
    onLogout,
    onRef,
}) => {
    const closeFlyout = useFlyoutClose()

    return (
        <UserMenuOverlay onRef={onRef}>
            <Box sx={{ py: 1 }}>
                <Box
                    component={C1Link}
                    href={routes.profile.index}
                    onClick={closeFlyout ?? undefined}
                    sx={{
                        display: 'block',
                        py: 2,
                        px: 2,
                        mx: 2,
                        borderRadius: 1,
                        textDecoration: 'none',
                        color: 'inherit',
                        overflow: 'hidden',

                        '&:hover': {
                            backgroundColor: 'primary.extraLight',
                        },
                        // Truncate name and metadata
                        '& .MuiTypography-root': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        },
                    }}
                >
                    <UserDisplayName user={user} readOnly maxWidth="100%" />
                </Box>

                <Divider sx={{ my: 1 }} />

                {!!hasAccountProvisioningFF && (
                    <NavItemDisplay
                        label={<FormattedMessage id="components_header_menu_vaults" defaultMessage="My vaults" />}
                        icon={<C1Icon icon={Lock} size="sm" sx={{ color: 'text.secondary' }} />}
                        href={routes.profile.vaults}
                    />
                )}
                {(!!hasApiKeysFF || !!hasDcrFF) && (
                    <NavItemDisplay
                        label={hasDcrFF
                            ? <FormattedMessage id="components_header_menu_profile_ai_api" defaultMessage="AI & API" />
                            : <FormattedMessage id="components_header_menu_profile_api" defaultMessage="API keys" />
                        }
                        icon={<C1Icon icon={Key} size="sm" sx={{ color: 'text.secondary' }} />}
                        href={routes.profile.api}
                    />
                )}
                {hasPersonalAndOrgNotificationSettingsFF && (
                    <NavItemDisplay
                        label={<FormattedMessage id="components_header_menu_notifications" defaultMessage="Notification settings" />}
                        icon={<C1Icon icon={Bell} size="sm" sx={{ color: 'text.secondary' }} />}
                        href={routes.profile.notifications}
                    />
                )}
                <NavItemDisplay
                    label={<FormattedMessage id="components_header_menu_logout" defaultMessage="Logout" />}
                    icon={<C1Icon icon={LogOut} size="sm" sx={{ color: 'text.secondary' }} />}
                    onClick={onLogout}
                />
            </Box>
        </UserMenuOverlay>
    )
}

type ContainerNavItemProps = {
    container: NavContainer
    isCollapsed: boolean
    isPersistent: boolean
    getCountForModule: (moduleId: string) => number | undefined
    isPathClaimedByAnyModule: () => boolean
}

const ContainerNavItem: FC<ContainerNavItemProps> = ({
    container,
    isCollapsed,
    isPersistent,
    getCountForModule,
    isPathClaimedByAnyModule,
}) => {
    const router = useRouter()
    const hasRole = useRoleChecker()
    const hasFeature = useUnsafeFeatureChecker()
    const isReadOnlySuperAdmin = useHasBuiltinRole(RoleID.ReadOnlySuperAdmin)
    const [flyoutRef, setFlyoutRef] = useState<HTMLDivElement | null>(null)
    const [mutedDotsCache] = useSessionStorageContext(MutedDotsCacheKey, {})
    const systemManagementWarning = useSystemManagementWarningContext()

    // Check container-level roles and features first
    // ReadOnlySuperAdmin bypasses role checks but still respects feature flags
    const containerMeetsRole = isReadOnlySuperAdmin || !container.roles?.length || container.roles.some(hasRole)
    const containerMeetsFeature = !container.features?.length || container.features.some(hasFeature)
    if (!containerMeetsRole || !containerMeetsFeature) return null

    const visibleModules = container.modules.filter((navModule) => {
        const meetsRole = isReadOnlySuperAdmin || !navModule.roles?.length || navModule.roles.some(hasRole)
        const meetsFeature = navModule.features?.length ? navModule.features.some(hasFeature) : true
        const noHideFeature = navModule.hideOnFeatures?.length
            ? !navModule.hideOnFeatures.some(hasFeature)
            : true
        return meetsRole && meetsFeature && noHideFeature && !navModule.placeholder
    })

    // Containers with custom flyouts always render; others need visible modules
    if (visibleModules.length === 0 && !container.customFlyout) return null

    // For containers with persistentPaths, check those for active state
    // Skip if a more specific module path already claims this path
    const pathClaimedByModule = isPathClaimedByAnyModule()
    const isActiveFromPersistentPaths = !pathClaimedByModule && (container.persistentPaths?.some((path) =>
        router.pathname.startsWith(path) || router.asPath.startsWith(path)) ?? false)

    const isActiveFromModules = visibleModules.some((navModule) => {
        const paths = [navModule.pathname, ...(navModule.selectedPaths || [])]
        return paths.some((path) => {
            const regex = RegExp(`^\\${path}.*$`, 'g')
            return router.pathname.match(regex) !== null || router.asPath.match(regex) !== null
        })
    })

    const isActive = isActiveFromPersistentPaths || isActiveFromModules

    // Count only modules that have count > 0 AND are not muted
    const hasUnmutedDot = visibleModules.some((navModule) => {
        const count = getCountForModule(navModule.id)
        const isMuted = mutedDotsCache?.[navModule.id] ?? false
        return count !== undefined && count > 0 && !isMuted
    })

    const defaultNavModule = visibleModules[0]
    const showModuleFlyout = !container.customFlyout && visibleModules.length > 1

    const Icon = container.icon
    const CustomFlyout = container.customFlyout

    // Show system management warning on Settings container
    const isSettingsContainer = container.id === 'settings'
    const showWarning = isSettingsContainer && systemManagementWarning.showWarning

    // Only show tooltip when collapsed AND no flyout is present (flyout header shows label)
    const hasFlyout = !!CustomFlyout || showModuleFlyout
    const showTooltip = isCollapsed && !hasFlyout

    return (
        <Tooltip
            title={showTooltip ? container.label : ''}
            placement="right"
            disableHoverListener={!showTooltip}
            enterDelay={0}
            leaveDelay={0}
        >
            <Box data-nav-id={container.id}>
                <NavItemDisplay
                    label={container.label}
                    icon={
                        <C1Icon
                            icon={Icon}
                            size="sm"
                            sx={{ color: isActive ? 'primary.extraDark' : 'text.primary' }}
                        />
                    }
                    isActive={isActive}
                    showDot={hasUnmutedDot && !showWarning}
                    showWarning={showWarning}
                    warningTooltipContent={systemManagementWarning.tooltipContent}
                    href={defaultNavModule?.pathname ?? container.defaultPath}
                    isCollapsed={isCollapsed}
                    isTopLevel
                    showSeparator={container.separatorBefore}
                    flyoutRef={isPersistent ? undefined : flyoutRef}
                    isPersistent={isPersistent}
                    wrapperSx={isPersistent ? {
                        '& > .nav-flyout': {
                            opacity: 1,
                            visibility: 'visible',
                            pointerEvents: 'auto',
                            zIndex: 1240, // Lower than hovered flyouts
                        },
                    } : undefined}
                >
                    {CustomFlyout && <CustomFlyout onRef={setFlyoutRef} label={container.label} isPersistent={isPersistent} />}
                    {showModuleFlyout && (
                        <ModuleFlyout
                            modules={visibleModules}
                            getCountForModule={getCountForModule}
                            onRef={setFlyoutRef}
                            label={container.label}
                        />
                    )}
                </NavItemDisplay>
            </Box>
        </Tooltip>
    )
}

type SidebarToggleButtonProps = {
    isCollapsed: boolean
    setIsCollapsed: (value: boolean) => void
}

const SidebarToggleButton: FC<SidebarToggleButtonProps> = ({ isCollapsed, setIsCollapsed }) => {
    const intl = useIntl()
    const [position, setPosition] = useState<{ top: number; right: number } | null>(null)

    useEffect(() => {
        const sidebar = document.getElementById('sidebar')
        const updatePosition = (): void => {
            if (sidebar) {
                const rect = sidebar.getBoundingClientRect()
                setPosition({
                    top: isCollapsed ? 12 : 16,
                    right: window.innerWidth - rect.right,
                })
            }
        }
        updatePosition()

        const resizeObserver = new ResizeObserver(updatePosition)
        if (sidebar) resizeObserver.observe(sidebar)

        window.addEventListener('resize', updatePosition)
        return () => {
            window.removeEventListener('resize', updatePosition)
            resizeObserver.disconnect()
        }
    }, [isCollapsed])

    const ariaLabel = isCollapsed
        ? intl.formatMessage({ id: 'nav_sidebar_expand', defaultMessage: 'Expand nav' })
        : intl.formatMessage({ id: 'nav_sidebar_collapse', defaultMessage: 'Collapse nav' })

    if (!position) return null

    return (
        <Box
            sx={{
                position: 'fixed',
                top: `${position.top}px`,
                right: `${position.right}px`,
                transform: isCollapsed ? `translateX(calc(50% - calc(${sidebarCollapsedWidth} / 2)))` : 'translateX(50%)',
                zIndex: 1240,
                ...getTransition('top, transform'),
            }}
        >
            <Stack
                alignItems="center"
                justifyContent="center"
                sx={{
                    height: '30px',
                    width: '30px',
                }}
            >
                <C1IconButton
                    onClick={(): void => setIsCollapsed(!isCollapsed)}
                    variant="outlined"
                    color="info"
                    aria-label={ariaLabel}
                    sx={{
                        height: '20px',
                        width: '20px',
                        borderColor: 'divider',
                        backgroundColor: 'background.default',

                        '&:hover': {
                            height: '28px',
                            width: '28px',
                            backgroundColor: 'background.default',

                            '& > .arrow-nudge-wrapper': {
                                transform: `translateX(${isCollapsed ? '3px' : '-3px'})`,
                            },
                        },
                    }}
                >
                    <Box
                        className="arrow-nudge-wrapper"
                        sx={{ ...getTransition('transform') }}
                    >
                        <C1Icon
                            icon={ArrowRight}
                            size="sm"
                            sx={{
                                fontSize: '12px',
                                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                                ...getTransition('transform'),
                                transitionDuration: '1s',
                            }}
                        />
                    </Box>
                </C1IconButton>
            </Stack>
        </Box>
    )
}

type SearchButtonProps = {
    onClick: () => void
}

const SearchButton: FC<SearchButtonProps> = ({ onClick }) => {
    const intl = useIntl()
    const [isCollapsedState] = useLocalStorageContext(COLLAPSED_SIDEBAR_NAV_STORAGE_KEY, false)
    const isCollapsed = isCollapsedState ?? false
    const isMac = typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent)

    return <Box sx={{ padding: `2px ${navItemWrapperXPaddingPx}px`, mb: 2 }}>
        <Tooltip
            title={isCollapsed ? `${intl.formatMessage({ id: 'spotlight_search', defaultMessage: 'Search' })} (${isMac ? '⌘' : 'Ctrl'}+K)` : ''}
            placement="right"
            disableHoverListener={!isCollapsed}
            enterDelay={0}
            leaveDelay={0}
        >
            <ListItemButton
                onClick={onClick}
                sx={{
                    borderRadius: 1,
                    py: (t) => `calc(${t.spacing(1)} - 1px)`,
                    px: (t) => `calc(${t.spacing(2)} - 1px)`,
                    minHeight: navItemMinHeightPx,
                    backgroundColor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    justifyContent: 'center',
                    ...getTransition('padding-left, padding-right'),
                    '&:hover': {
                        backgroundColor: 'action.hover',
                    },
                }}
            >
                <ListItemIcon
                    sx={{ minWidth: 0, mr: 2, transform: isCollapsed ? 'translateX(5px)' : 'translateX(0px)', ...getTransition('transform') }}
                >
                    <C1Icon icon={Search} size="sm" sx={{ color: 'text.secondary' }} />
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Stack direction="row" alignItems="center" justifyContent="flex-end" sx={{ overflow: 'hidden' }}>
                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: 11 }}>
                                {isMac ? '⌘K' : 'Ctrl+K'}
                            </Typography>
                        </Stack>
                    }
                    sx={{
                        m: 0,
                        opacity: isCollapsed ? 0 : 1,
                        transitionProperty: 'opacity',
                        transitionDuration: '2s',
                        transitionTimingFunction: sidebarTransitionEasing,
                    }}
                />
            </ListItemButton>
        </Tooltip>
    </Box>
}
